// /src/components/private/GuestAccessRequestsPopup.jsx

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../authContext';
import { useSecurity } from '../../SecurityContext';
import useGuestAccessRequestsSocket from '../../hooks/useGuestAccessRequestsSocket';
import {
  acceptGuestAccessRequest,
  fetchPendingGuestAccessRequests,
  rejectGuestAccessRequest,
} from '../../pages/guestAccess/guestAccessApi';
import {
  getPendingGuestAccessCount,
  setPendingGuestAccessCount,
  subscribeGuestAccessPendingList,
  subscribePendingGuestAccessCount,
} from '../../pages/guestAccess/guestAccessPendingStore';

export default function GuestAccessRequestsPopup() {
  const { isLoggedIn } = useAuth();
  const { uiHasPermission, userHasPermission } = useSecurity();
  const canRead = uiHasPermission('private.navbar.guestAccessRequests', 'read');
  const canWrite = userHasPermission('private.navbar.guestAccessRequests', 'write');

  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [forceChipPortal, setForceChipPortal] = useState(false);
  const [pendingCount, setPendingCount] = useState(() => getPendingGuestAccessCount());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useGuestAccessRequestsSocket({
    enabled: Boolean(isLoggedIn && canRead),
    isLoggedIn: Boolean(isLoggedIn),
    mode: 'admin',
  });

  useEffect(() => subscribePendingGuestAccessCount(setPendingCount), []);

  const closePopup = useCallback(() => {
    setDismissed(true);
    setOpen(false);
  }, []);

  const openPopup = useCallback(() => {
    setDismissed(false);
    setOpen(true);
  }, []);

  // Auto-open on reload when there are pending guests.
  useEffect(() => {
    if (!dismissed && pendingCount > 0 && !open) {
      setOpen(true);
    }
  }, [dismissed, pendingCount, open]);

  // If another modal/backdrop is opened, render the chip in a fixed portal
  // so it stays clickable (stacking contexts can block clicks otherwise).
  useEffect(() => {
    const selector =
      '.modal, .recurrence-modal, .calendar-template-modal, .modal-overlay';

    const check = () => {
      setForceChipPortal(Boolean(document.querySelector(selector)));
    };

    let rafId = null;
    const scheduleCheck = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        check();
      });
    };

    check();
    const obs = new MutationObserver(scheduleCheck);
    obs.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      obs.disconnect();
    };
  }, []);

  const loadList = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const data = await fetchPendingGuestAccessRequests();
      if (data?.ok) {
        setRows(Array.isArray(data.data) ? data.data : []);
        if (typeof data.pendingCount === 'number') {
          setPendingGuestAccessCount(data.pendingCount);
        }
      }
    } catch (err) {
      console.error('[GuestAccessRequestsPopup] load failed', err);
    } finally {
      setLoading(false);
    }
  }, [canRead]);

  useEffect(() => {
    if (!open) return undefined;
    void loadList();
    return subscribeGuestAccessPendingList(() => {
      void loadList();
    });
  }, [open, loadList]);

  const onAccept = async (id) => {
    if (!canWrite || busyId != null) return;
    setBusyId(id);
    try {
      const data = await acceptGuestAccessRequest(id);
      if (data?.ok && typeof data.pendingCount === 'number') {
        setPendingGuestAccessCount(data.pendingCount);
      }
      await loadList();
    } catch (err) {
      console.error('[GuestAccessRequestsPopup] accept failed', err);
    } finally {
      setBusyId(null);
    }
  };

  const onReject = async (id) => {
    if (!canWrite || busyId != null) return;
    setBusyId(id);
    try {
      const data = await rejectGuestAccessRequest(id);
      if (data?.ok && typeof data.pendingCount === 'number') {
        setPendingGuestAccessCount(data.pendingCount);
      }
      await loadList();
    } catch (err) {
      console.error('[GuestAccessRequestsPopup] reject failed', err);
    } finally {
      setBusyId(null);
    }
  };

  if (!isLoggedIn || !canRead) return null;

  const showGuestChip = pendingCount > 0 && !open && dismissed;

  const overlay = open
    ? createPortal(
      <div
        className="guest-access-popup-overlay"
        role="presentation"
        onClick={closePopup}
      >
        <div
          className="guest-access-popup"
          role="dialog"
          aria-modal="true"
          aria-label="Guest access requests"
          data-resource="private.navbar.guestAccessRequests"
          data-action="read"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="guest-access-popup__header">
            <div className="guest-access-popup__header-text">
              <p className="guest-access-popup__eyebrow">
                Pending access
                {pendingCount > 0 ? (
                  <span className="guest-access-popup__count">{pendingCount}</span>
                ) : null}
              </p>
              <h2 className="guest-access-popup__title">Guest requests</h2>
            </div>
            <button
              type="button"
              className="guest-access-popup__minimize"
              onClick={closePopup}
              aria-label="Minimize guest requests"
            >
              Minimize
            </button>
          </header>

          {loading ? (
            <p className="guest-access-popup__empty">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="guest-access-popup__empty">No pending Guest requests.</p>
          ) : (
            <ul className="guest-access-popup__list">
              {rows.map((row) => (
                <li key={row.id} className="guest-access-popup__item">
                  <div className="guest-access-popup__item-layout">
                    <div className="guest-access-popup__item-main">
                      <div className="guest-access-popup__identity">
                        <span className="guest-access-popup__handle">
                          {row.handle ? `@${row.handle}` : 'No handle'}
                        </span>
                        <span className="guest-access-popup__discord">
                          {row.discordName || '—'}
                        </span>
                        {row.rejectionCount > 0 ? (
                          <span
                            className="guest-access-popup__refusal-badge"
                            title="Past refusals"
                          >
                            {row.rejectionCount}
                          </span>
                        ) : null}
                      </div>
                      <p className="guest-access-popup__meta">
                        Requested{' '}
                        {row.createdAt
                          ? new Date(row.createdAt).toLocaleString()
                          : '—'}
                      </p>
                    </div>
                    <div
                      className="guest-access-popup__decision"
                      role="group"
                      aria-label="Grant or reject request"
                    >
                      <button
                        type="button"
                        className="guest-access-popup__decision-btn guest-access-popup__decision-btn--grant"
                        disabled={!canWrite || busyId === row.id}
                        onClick={() => onAccept(row.id)}
                        data-resource="private.navbar.guestAccessRequests"
                        data-action="write"
                        data-route={`/api/admin/guest-access/${row.id}/accept`}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        className="guest-access-popup__decision-btn guest-access-popup__decision-btn--reject"
                        disabled={!canWrite || busyId === row.id}
                        onClick={() => onReject(row.id)}
                        data-resource="private.navbar.guestAccessRequests"
                        data-action="write"
                        data-route={`/api/admin/guest-access/${row.id}/reject`}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      {showGuestChip ? (
        forceChipPortal ? (
          createPortal(
            <div className="guest-access-chip-portal">
              <button
                type="button"
                className="guest-access-nav-trigger guest-access-nav-trigger--tilt"
                onClick={openPopup}
                data-resource="private.navbar.guestAccessRequests"
                data-action="read"
                data-route="/api/admin/guest-access/pending"
                title="Guest access requests"
                aria-label="Guest access requests"
              >
                <span className="guest-access-nav-trigger__label">guest</span>
              </button>
            </div>,
            document.body
          )
        ) : (
          <div className="guest-access-nav-wrapper">
            <button
              type="button"
              className="guest-access-nav-trigger guest-access-nav-trigger--tilt"
              onClick={openPopup}
              data-resource="private.navbar.guestAccessRequests"
              data-action="read"
              data-route="/api/admin/guest-access/pending"
              title="Guest access requests"
              aria-label="Guest access requests"
            >
              <span className="guest-access-nav-trigger__label">guest</span>
            </button>
          </div>
        )
      ) : null}
      {overlay}
    </>
  );
}
